import { config } from "assets/config/config";
import slug from "slug";
import { cleanDescription } from "utils/headUtil";
import { getShopName, HOMEPAGE_SEO, normalizeLang, pickLocalized } from "utils/seo/i18n";
import { getProductOfferPrice } from "utils/seo/productPrice";

export const getDefaultHeadTags = (lang = "pl", robots = "index, follow") => {
  return ([
    { property: "og:site_name", content: getShopName(lang) },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: lang === 'en' ? "en_US" : "pl_PL" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:site", content: "@chopininstitute" },
    { name: "robots", content: robots }
  ])
}

const NO_INDEX_PAGE_TITLES = {
  cart:         { pl: "Koszyk",                   en: "Cart" },
  cartLogin:    { pl: "Logowanie i rejestracja",  en: "Login & Registration" },
  cartDelivery: { pl: "Dostawa zamówienia",       en: "Order Delivery" },
  cartSummary:  { pl: "Podsumowanie zamówienia",  en: "Order Summary" },
  cartFinal:    { pl: "Zamówienie złożone",       en: "Order Confirmation" },
  registration: { pl: "Rejestracja",              en: "Registration" },
  account:      { pl: "Twoje konto",              en: "Your Account" },
  orders:       { pl: "Moje zamówienia",          en: "My Orders" },
  orderDetail:  { pl: "Szczegóły zamówienia",     en: "Order Details" },
  favorites:    { pl: "Ulubione produkty",        en: "Favorites" }
};

const NO_INDEX_PAGE_DESCRIPTIONS = {
  cart:         { pl: "Twój koszyk w oficjalnym sklepie Narodowego Instytutu Fryderyka Chopina.",                                  en: "Your cart at the official Fryderyk Chopin Institute store." },
  cartLogin:    { pl: "Zaloguj się lub zarejestruj, aby kontynuować zamówienie w sklepie NIFC.",                                   en: "Log in or register to continue your order at the NIFC Store." },
  cartDelivery: { pl: "Wybierz adres oraz sposób dostawy zamówienia w sklepie NIFC.",                                              en: "Choose the delivery address and shipping method for your NIFC Store order." },
  cartSummary:  { pl: "Sprawdź podsumowanie zamówienia przed jego złożeniem w sklepie NIFC.",                                      en: "Review your order summary before placing it at the NIFC Store." },
  cartFinal:    { pl: "Dziękujemy za zakupy w sklepie Narodowego Instytutu Fryderyka Chopina.",                                    en: "Thank you for shopping at the Fryderyk Chopin Institute store." },
  registration: { pl: "Załóż konto w oficjalnym sklepie Narodowego Instytutu Fryderyka Chopina.",                                  en: "Create an account at the official Fryderyk Chopin Institute store." },
  account:      { pl: "Panel klienta sklepu Narodowego Instytutu Fryderyka Chopina.",                                              en: "Customer panel of the Fryderyk Chopin Institute store." },
  orders:       { pl: "Lista Twoich zamówień złożonych w sklepie NIFC.",                                                           en: "List of your orders placed at the NIFC Store." },
  orderDetail:  { pl: "Szczegóły zamówienia złożonego w sklepie NIFC.",                                                            en: "Details of your order placed at the NIFC Store." },
  favorites:    { pl: "Twoja lista ulubionych produktów w sklepie NIFC.",                                                          en: "Your list of favourite products at the NIFC Store." }
};

export const getHeadTagsNoIndex = (lang = "pl", currentPath = "", pageKey = "") => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;
  const shopName = getShopName(lang);

  const titleDict = NO_INDEX_PAGE_TITLES[pageKey];
  const descDict = NO_INDEX_PAGE_DESCRIPTIONS[pageKey];

  const title = titleDict ? (titleDict[lang] || titleDict.pl) : shopName;
  const description = descDict ? (descDict[lang] || descDict.pl) : "";

  const finalTitle = title.includes(shopName) ? title : `${title} - ${shopName}`;
  const image = `${baseUrl}/favicon/og_image.jpg`;

  const tags = [
    ...getDefaultHeadTags(lang, "noindex, nofollow"),
    { title: finalTitle },
    { rel: "canonical", href: pageUrl },
    { property: "og:type", content: "website" },
    { property: "og:title", content: finalTitle },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:url", content: pageUrl },
    { name: "twitter:image", content: image }
  ];

  if (description) {
    tags.push({ name: "description", content: description });
    tags.push({ property: "og:description", content: description });
    tags.push({ name: "twitter:description", content: description });
  }

  return tags;
};

export const getHeadTagsTextPage = (data, lang = "pl", currentPath = "") => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;
  const shopName = getShopName(lang);

  const pageTitle = (data && data.title) || (lang === 'pl' ? "Informacje" : "Information");
  const finalTitle = pageTitle.includes(shopName) ? pageTitle : `${pageTitle} - ${shopName}`;

  const rawText = (data && data.text) || "";
  const cleaned = cleanDescription(rawText);
  const fallbackDesc = lang === 'pl'
    ? "Informacje w oficjalnym sklepie Narodowego Instytutu Fryderyka Chopina."
    : "Information at the official Fryderyk Chopin Institute store.";
  const description = (cleaned && cleaned.length > 10 ? cleaned.substring(0, 160) : fallbackDesc);

  const image = (data && data.image) || `${baseUrl}/favicon/og_image.jpg`;

  return [
    ...getDefaultHeadTags(lang),
    { title: finalTitle },
    { name: "description", content: description },
    { rel: "canonical", href: pageUrl },
    { property: "og:type", content: "article" },
    { property: "og:title", content: finalTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:url", content: pageUrl },
    { name: "twitter:image", content: image }
  ];
};

export const getHeadTagsHomepage = (lang = "pl") => {
  const baseUrl = config.APP_MAIN_URL;
  const locale = normalizeLang(lang);
  const homepage = pickLocalized(locale, HOMEPAGE_SEO);
  const pageUrl = `${baseUrl}/${locale}`;

  return ([
    ...getDefaultHeadTags(locale),
    { title: homepage.title },
    { rel: "canonical", href: pageUrl },
    { name: "description", content: homepage.description },
    { property: "og:title", content: homepage.title },
    { property: "og:description", content: homepage.description },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: `${baseUrl}/favicon/og_image.jpg` },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: homepage.title },
    { name: "twitter:description", content: homepage.description },
    { name: "twitter:url", content: pageUrl },
    { name: "twitter:image", content: `${baseUrl}/favicon/twitter_image.jpg` },
    { name: "twitter:image:alt", content: homepage.twitterImageAlt }
  ])
}

export const getHeadTagsProduct = (product, lang = 'pl', currency = 'PLN') => {
  const baseUrl = config.APP_MAIN_URL;

  let nameForSlug = product.name || "";
  if (product.number && nameForSlug.includes(product.number)) {
    nameForSlug = nameForSlug.replace(product.number, "").trim();
  }

  const productSlug = nameForSlug ? slug(nameForSlug) : '';
  const productUrl = `${baseUrl}/${lang}/produkt/${product.id}-${productSlug}`;

  const rawDescription = product.description || product.short_description || "";

  const description = cleanDescription(rawDescription)

  const metaDescription = description.substring(0, 160);

  const { price, currency: priceCurrency } = getProductOfferPrice(product, currency);
  const image = product.thumbnail_image;

  const groupId = Number(product.id_group);
  const catalogNumber = (product.number || "").toUpperCase();
  const hasAudios = !!(product.audios && product.audios.length > 0);

  let ogType = "product";

  if (catalogNumber.includes("NIFCDVD") || catalogNumber.includes("NIFCBLU")) {
    ogType = "video.other";
  } else if (groupId === 2 || hasAudios || catalogNumber.includes("NIFCCD")) {
    ogType = "music.album";
  } else if (groupId === 1 || groupId === 14 || groupId === 18 || groupId === 23) {
    ogType = "book";
  }

  const shopName = getShopName(lang);

  const tags = [
    ...getDefaultHeadTags(lang),
    { title: `${product.name} | ${shopName}` },
    { name: "description", content: metaDescription },
    { rel: "canonical", href: productUrl },
    { property: "og:type", content: ogType },
    { property: "og:title", content: product.name },
    { property: "og:description", content: metaDescription },
    { property: "og:url", content: productUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: product.name },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:image", content: image },
    { property: "product:availability", content: Number(product.quantity) > 0 ? "instock" : "oos" }
  ];

  if (price !== undefined && price !== null && price !== "") {
    tags.push({ property: "product:price:amount", content: String(price) });
    tags.push({ property: "product:price:currency", content: priceCurrency });
  }

  if (product.year_published) {
    tags.push({ property: "original_publication_date", content: product.year_published });
  }

  const itemsIsObject = product.items && !Array.isArray(product.items);
  const productIsbn = product.isbn || (itemsIsObject ? product.items.isbn : null);

  if (ogType === "book") {
    if (productIsbn) tags.push({ property: "book:isbn", content: productIsbn });

    let authorName = "";
    if (product.author && product.author[0]) {
      const a = product.author[0];
      authorName = a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : (a.name || "");
    }

    const finalAuthor = authorName || (itemsIsObject ? product.items.entry : "");
    if (finalAuthor) tags.push({ property: "book:author", content: finalAuthor });
  }

  let productSize = product.size;
  if (!productSize && product.variants && product.variants.length > 0) {
    productSize = product.variants.map(v => v.variant_name).join(", ");
  }

  const pages = product.pages_number || (itemsIsObject ? product.items.pages_num : null);

  if (product.language) tags.push({ name: "product:language", content: product.language });
  if (product.material) tags.push({ name: "product:material", content: product.material });
  if (product.color_options) tags.push({ name: "product:color", content: product.color_options });
  if (productSize) tags.push({ name: "product:size", content: productSize });
  if (product.format) tags.push({ name: "product:format", content: product.format });
  if (pages) tags.push({ name: "product:pages", content: pages });
  if (product.cover) tags.push({ name: "product:cover", content: product.cover });
  if (product.additions) tags.push({ name: "product:additional_info", content: product.additions });
  if (product.patterns) tags.push({ name: "product:pattern", content: product.patterns });
  if (product.capacity) tags.push({ name: "product:capacity", content: product.capacity });

  if (product.dimensions) {
    tags.push({ name: "product:dimensions", content: product.dimensions.trim() });
  }

  return tags;
};

export const getHeadTagsProductList = (data, lang = 'pl', currentPath = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const group = data.group || data.category?.group || {};
  const category = data.category || {};
  const meta = data._meta || {};
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const categoryUrl = `${baseUrl}${cleanPath}`;

  const shopName = getShopName(lang);
  const pageSuffix = meta.currentPage > 1
    ? (lang === 'pl' ? ` (Strona ${meta.currentPage})` : ` (Page ${meta.currentPage})`)
    : "";

  let title = group.seo_title || category.seo_title;
  let description = group.seo_description || category.seo_description;

  if (currentPath.includes('/promocje')) {
    title = title || (lang === 'pl' ? "Promocje" : "Special Offers");
    description = description || (lang === 'pl'
      ? "Sprawdź aktualne promocje i oferty specjalne w oficjalnym sklepie Narodowego Instytutu Fryderyka Chopina."
      : "Check out current promotions and special offers at the official NIFC Store.");
  } else if (currentPath.includes('/wyszukiwanie')) {
    title = title || (lang === 'pl' ? "Wyniki wyszukiwania" : "Search Results");
    description = description || (lang === 'pl'
      ? "Przeszukaj ofertę oficjalnego sklepu NIFC. Znajdź interesujące Cię wydawnictwa, płyty i pamiątki chopinowskie."
      : "Search the official NIFC store. Find publications, recordings, and Chopin-related souvenirs.");
  } else {
    title = title || group.name || category.name || (lang === 'pl' ? "Produkty" : "Products");
    description = description || (lang === 'pl'
      ? `Oficjalny sklep Narodowego Instytutu Fryderyka Chopina. Kategoria: ${group.name || category.name || "Produkty"}.`
      : `Official Store of the Fryderyk Chopin Institute. Category: ${group.name || category.name || "Products"}.`);
  }

  if (!title.includes(shopName)) {
    title = `${title} - ${shopName}`;
  }

  const finalTitle = `${title}${pageSuffix}`;
  const image = group.seo_image || category.seo_image || group.banner_image || `${baseUrl}/favicon/og_image.jpg`;

  const tags = [
    ...getDefaultHeadTags(lang),
    { title: finalTitle },
    { name: "description", content: description },
    { rel: "canonical", href: categoryUrl },
    { property: "og:type", content: "website" },
    { property: "og:title", content: finalTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: categoryUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:url", content: categoryUrl },
    { name: "twitter:image", content: image }
  ];

  if (meta.pageCount > 1) {
    if (meta.currentPage < meta.pageCount) {
      tags.push({ rel: "next", href: `${categoryUrl}?page=${meta.currentPage + 1}` });
    }
    if (meta.currentPage > 1) {
      tags.push({ rel: "prev", href: `${categoryUrl}?page=${meta.currentPage - 1}` });
    }
  }

  return tags;
};

export const getHeadTagsPeopleList = (lang = 'pl', currentPath = '', type = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;

  const shopName = getShopName(lang);

  const translations = {
    artysci: {
      pl: { title: "Artyści", desc: "Poznaj sylwetki wybitnych artystów związanych z Narodowym Instytutem Fryderyka Chopina." },
      en: { title: "Artists", desc: "Discover the profiles of outstanding artists associated with The Fryderyk Chopin Institute." }
    },
    autorzy: {
      pl: { title: "Autorzy", desc: "Lista autorów i twórców publikacji dostępnych w naszym sklepie." },
      en: { title: "Authors", desc: "List of authors and creators of publications available in our store." }
    },
    "autorzy-redaktorzy": {
      pl: { title: "Autorzy i Redaktorzy", desc: "Zbiór autorów oraz redaktorów naukowych wydawnictw NIFC." },
      en: { title: "Authors & Editors", desc: "A collection of authors and academic editors of NIFC publications." }
    },
    dyrygenci: {
      pl: { title: "Dyrygenci", desc: "Mistrzowie batuty współpracujący z NIFC przy nagraniach i koncertach." },
      en: { title: "Conductors", desc: "Masters of the baton cooperating with NIFC on recordings and concerts." }
    },
    instytucje: {
      pl: { title: "Instytucje i Zespoły", desc: "Instytucje, orkiestry oraz zespoły muzyczne obecne w katalogu NIFC." },
      en: { title: "Institutions & Ensembles", desc: "Institutions, orchestras, and musical ensembles in the NIFC catalogue." }
    }
  };

  const currentType = translations[type] || translations['artysci'];
  const content = pickLocalized(lang, currentType);

  let finalTitle = content.title;
  if (!finalTitle.includes(shopName)) {
    finalTitle = `${finalTitle} - ${shopName}`;
  }

  const image = `${baseUrl}/favicon/og_image.jpg`;

  return [
    ...getDefaultHeadTags(lang),
    { title: finalTitle },
    { name: "description", content: content.desc },
    { rel: "canonical", href: pageUrl },
    { property: "og:type", content: "website" },
    { property: "og:title", content: finalTitle },
    { property: "og:description", content: content.desc },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: content.desc },
    { name: "twitter:url", content: pageUrl },
    { name: "twitter:image", content: image }
  ];
};

export const getHeadTagsPersonDetail = (data, lang = 'pl', currentPath = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;
  const shopName = getShopName(lang);
  const fullName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
  const category = data.category || (lang === 'pl' ? "Osoba" : "Person");
  const finalTitle = `${fullName} - ${category} - ${shopName}`;

  let description = cleanDescription(data.lead);
  if (!description || description.length < 10) {
    description = cleanDescription(data.biography).substring(0, 160);
  } else {
    description = description.substring(0, 160);
  }

  const image = data.image || `${baseUrl}/favicon/og_image.jpg`;

  return [
    ...getDefaultHeadTags(lang),
    { title: finalTitle },
    { name: "description", content: description },
    { rel: "canonical", href: pageUrl },
    { property: "og:type", content: "profile" },
    { property: "og:title", content: finalTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: image },
    { property: "profile:first_name", content: data.first_name || "" },
    { property: "profile:last_name", content: data.last_name || "" },
    { property: "profile:gender", content: data.gender || "" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image }
  ];
};

export const getHeadTagsInstitutionDetail = (data, lang = 'pl', currentPath = '') => {
  const baseUrl = config.APP_MAIN_URL;
  const cleanPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
  const pageUrl = `${baseUrl}${cleanPath}`;
  const shopName = getShopName(lang);

  const title = data.title || "";
  const finalTitle = `${title} - ${shopName}`;

  let description = cleanDescription(data.text).substring(0, 160);

  const image = data.image || `${baseUrl}/favicon/og_image.jpg`;

  return [
    ...getDefaultHeadTags(lang),
    { title: finalTitle },
    { name: "description", content: description },
    { rel: "canonical", href: pageUrl },
    { property: "og:type", content: "website" },
    { property: "og:title", content: finalTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: pageUrl },
    { property: "og:image", content: image },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalTitle },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image }
  ];
};